import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import axiosInstance from '@common/services/axiosIntance/axiosService';
import { downloadFile } from '@common/utils/handleActions/ActionItem';
import appConfig from '../../appConfig.json';
import { GptConversationServiceTypeEnum } from '@common/constants/gpt-conversation.constant';

type GetAssistantHistoryRequest = {
  serviceType: GptConversationServiceTypeEnum.openAiAssistant;
  threadId: string;
  assistantId: string;
};
type GetResponsesHistoryRequest = {
  serviceType: GptConversationServiceTypeEnum.openAiResponses;
  responseId: string;
};

type GetChatHistoryRequest = {
  apiKey: string | number; // App variable keys
} & (GetAssistantHistoryRequest | GetResponsesHistoryRequest);

type BaseMessage = {
  id: string;
  senderType: 'user' | 'ai' | 'error';
  message: string;
  createdAt: number; // unix
};
type MessageText = BaseMessage & {
  messageType: 'text';
  attachments?: undefined;
};
export type MessageFile = BaseMessage & {
  messageType: 'file';
  attachments: { fileId: string; containerId?: string };
};
type MessageUrl = BaseMessage & {
  messageType: 'url';
  attachments: { url: string };
};
type MessageImage = BaseMessage & {
  messageType: 'image';
  attachments: { url: string };
};
export type IMessage = MessageText | MessageFile | MessageUrl | MessageImage;
type GetGptMessageHistoryResponse = IMessage[];
type CheckThreadRunningRequest = {
  apiKeyId: string | number;
  threadId: string;
};
type CheckThreadRunningResponse = {
  isRunning: boolean;
};
export const useGptConversationAction = (appId: string) => {
  const getMessageHistory = async (data: GetChatHistoryRequest) => {
    try {
      const response = await axiosInstance({
        url: API_ENDPOINTS.GPT_CONVERSATION_CHAT_HISTORY,
        method: 'GET',
        params: { ...data, appId },
      }).then((r) => r.data as GetGptMessageHistoryResponse);
      return response;
    } catch (e: any) {
      console.error('=== getMessageHistory error===', e);
      return [];
    }
  };
  const onDownloadFile = async ({
    apiKey,
    attachments,
  }: Pick<MessageFile, 'attachments'> & {
    apiKey: string;
  }) => {
    try {
      const fileDetail = await axiosInstance({
        url: `${API_ENDPOINTS.GPT_CONVERSATION_FILE}/${attachments.fileId}`,
        method: 'GET',
        params: { apiKey, appId, containerId: attachments.containerId },
      }).then(
        (r) =>
          r.data as {
            filename: string;
          }
      );
      // download file
      const fileUrl = new URL(
        `${API_ENDPOINTS.GPT_CONVERSATION_FILE_CONTENT}/${attachments.fileId}`,
        appConfig.baseUrl
      );
      fileUrl.searchParams.set('apiKey', apiKey);
      fileUrl.searchParams.set('appId', appId);
      if (attachments.containerId) {
        fileUrl.searchParams.set('containerId', attachments.containerId);
      }
      await downloadFile({
        response: {
          url: fileUrl.toString(),
          fileName: fileDetail.filename,
        },
      });
    } catch (err) {
      console.error('=== downloadFile error ===', err);
      return null;
    }
  };
  /**
   * wait until thread is not running
   */
  const checkThreadRunning = async (data: CheckThreadRunningRequest) => {
    const { apiKeyId, threadId } = data;
    while (true) {
      const response = await axiosInstance({
        url: API_ENDPOINTS.GPT_CONVERSATION_CHAT_OPEN_AI_ASSISTANT,
        method: 'POST',
        data: { apiKeyId, threadId, appId },
      })
        .then((r) => r.data as CheckThreadRunningResponse)
        .catch((e) => {
          console.error('=== checkThreadRunning error ===', e.response.data);
          return { isRunning: false };
        });
      if (!response.isRunning) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  };
  return { getMessageHistory, onDownloadFile, checkThreadRunning };
};
export type GptConversationActionType = ReturnType<
  typeof useGptConversationAction
>;
